/**
 * PWAInstallPrompt — the install ask (IB-1, rebuilt as v2 under UI-POLISH-3).
 *
 * WHAT CHANGED IN v2 AND WHY.
 *
 * SPLIT BY PLATFORM, because the two platforms genuinely differ. Chromium hands us a real install
 * event, so Android and desktop Chrome get ONE BUTTON that opens the native dialog — no menus, no
 * instructions, nothing to read. iOS has no such event and never will, so it gets the actual steps
 * with the Share glyph instead of a button that would have to apologise when pressed. Showing
 * Android users instructions they do not need, or iOS users a button that cannot work, are the two
 * ways this goes wrong; they are now separate paths rather than one compromise.
 *
 * SHOWS ON EVERY VIEWPORT. v1 rendered null at ≥1024px, so a laptop was never offered the app at
 * all — and desktop Chrome installs PWAs perfectly well.
 *
 * TWO SECONDS AFTER LOAD, not immediately: arriving simultaneously with the page reads as a popup.
 *
 * SNOOZE IS ONE DAY [JOB B S5X item 5, owner ruling] — it was fourteen at IB-1 and three under
 * UI-POLISH-3. Fourteen was long enough that a dismissal outlived the decision behind it, and because
 * the stamp lives in localStorage it SURVIVES uninstalling the app, which is how a genuinely clean
 * device ended up with no banner at all. Any longer stamp — 14-day or 3-day — is rewritten to the
 * 24-hour window on first load, so an existing dismissal converts instead of holding someone out
 * under a rule the code no longer implements.
 *
 * ⚠️ A DAILY BANNER IS ONLY REASONABLE BECAUSE THE DELIBERATE ROUTE NOW EXISTS. `InstallAppButton`
 * sits in the login footer, every shell footer AND — since S5X — permanently in every portal header,
 * and installing is what ends the asking: `isStandalone()` is checked before either surface renders.
 */
import { useState, useEffect } from 'react';
import { Share, Plus, X, Download } from 'lucide-react';
import {
  isStandalone,
  isIos,
  canInstall,
  onInstallPromptAvailable,
  showInstallPrompt,
} from '../../utils/pwa';

const DISMISS_KEY = 'aisistoo_install_dismissed_until';
/**
 * UI-POLISH-3 took 14 days → 3. [JOB B S5X item 5] takes 3 days → ONE, on the owner's ruling: the
 * banner comes back the next day and keeps coming back until the app is installed.
 *
 * ⚠️ IT IS NOT A NAG ANY MORE PRECISELY BECAUSE THE DELIBERATE ROUTE NOW EXISTS. A daily banner would
 * be the wrong call on its own; beside a permanent "Install app" button in every portal header it is a
 * reminder someone can answer once and never see again — installing is what ends it, and
 * `isStandalone()` below is checked before anything renders.
 */
const DISMISS_MS = 24 * 60 * 60 * 1000;
/** Anything beyond this came from an older rule (14 days, then 3) and is capped back on read. */
const MAX_SNOOZE_MS = DISMISS_MS;
const SHOW_AFTER_MS = 2000;

/**
 * Read the snooze, converting any legacy 14-day stamp in place.
 *
 * Without the conversion, everyone who dismissed v1 keeps the banner suppressed for up to another
 * eleven days after this ships — they would never see the change, and from their side the fix
 * simply would not have happened.
 */
function isSnoozed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const until = Number(raw);
    if (!Number.isFinite(until)) return false;
    if (until - Date.now() > MAX_SNOOZE_MS) {
      const capped = Date.now() + MAX_SNOOZE_MS;
      localStorage.setItem(DISMISS_KEY, String(capped));
      return Date.now() < capped;
    }
    return Date.now() < until;
  } catch {
    return false;
  }
}

function snooze(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
  } catch {
    /* storage unavailable — degrade to hiding for this session only */
  }
}

export function PWAInstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [oneTap, setOneTap] = useState(() => canInstall());
  const [showSteps, setShowSteps] = useState(false);
  const ios = isIos();

  useEffect(() => {
    if (isStandalone()) return;   // already installed — never ask
    if (isSnoozed()) return;

    const onInstalled = () => setVisible(false);
    window.addEventListener('appinstalled', onInstalled);
    // Fires immediately if the event was captured at module load (ONBOARD-FIX-1 A2.2).
    const unsub = onInstallPromptAvailable(() => setOneTap(true));

    const timer = window.setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    return () => {
      window.removeEventListener('appinstalled', onInstalled);
      unsub();
      window.clearTimeout(timer);
    };
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    snooze();
    setVisible(false);
  };

  const install = async () => {
    const outcome = await showInstallPrompt();
    if (outcome === 'accepted') setVisible(false);
    else if (outcome === 'dismissed') dismiss();
    // 'unavailable' = Chrome is withholding the event (cooldown after an uninstall, or it was
    // already spent). Show the short steps rather than a button that did nothing.
    else setShowSteps(true);
  };

  return (
    <div
      role="banner"
      aria-label="Install Aisistoo Lite"
      style={{
        position: 'fixed',
        bottom: 'var(--bottom-nav-h, 68px)',
        left: 0,
        right: 0,
        zIndex: 'var(--z-toast)' as unknown as number,
        padding: 16,
        background: 'var(--surface)',
        borderTop: '1.5px solid var(--border)',
        boxShadow: '0 -8px 32px rgba(79,70,229,0.12)',
        animation: 'slideUp 0.3s ease',
      }}
    >
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', maxWidth: 520, margin: '0 auto' }}>
        <div
          style={{
            width: 48, height: 48, borderRadius: 14, flexShrink: 0,
            background: 'linear-gradient(135deg,#4F46E5,#7C3AED)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(79,70,229,0.3)',
          }}
        >
          <Download size={22} color="#fff" />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: 'var(--t1)', lineHeight: 1.35 }}>
            For Better Experience Install “Aisistoo Lite” App
          </p>

          {/* iOS: the steps ARE the content. There is no event to offer, so no button pretends there is. */}
          {ios ? (
            <ol style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, color: 'var(--t2)', lineHeight: 1.7 }}>
              <li>Tap <Share size={12} style={{ verticalAlign: -1 }} /> <strong>Share</strong> below</li>
              <li>Choose <Plus size={12} style={{ verticalAlign: -1 }} /> <strong>Add to Home Screen</strong></li>
              <li>Tap <strong>Add</strong></li>
            </ol>
          ) : showSteps ? (
            <>
              <ol style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, color: 'var(--t2)', lineHeight: 1.7 }}>
                <li>Open the browser menu (<strong>⋮</strong>)</li>
                <li>Tap <strong>Install app</strong> / <strong>Add to Home screen</strong></li>
              </ol>
              <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--t4)' }}>
                Your browser isn’t offering one-tap install right now — normal for a while after uninstalling.
              </p>
            </>
          ) : (
            <button
              type="button"
              onClick={install}
              style={{
                marginTop: 10, padding: '9px 18px', borderRadius: 10, border: 'none',
                background: 'var(--p)', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: 'pointer',
              }}
            >
              {oneTap ? 'Install' : 'Install app'}
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Not now"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t4)', padding: 2, flexShrink: 0 }}
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

export default PWAInstallPrompt;
